/** Title-contact status from opportunity customers (ON TITLE / OnTitleCheck). */



import { opportunityContactNumericId } from './titleSearchOwners';



function asRecord(value: unknown): Record<string, unknown> | null {

    return value && typeof value === 'object' && !Array.isArray(value)

        ? (value as Record<string, unknown>)

        : null;

}



function normalizePersonName(value: unknown): string {

    return String(value ?? '')

        .trim()

        .toLowerCase()

        .replace(/\s+/g, ' ');

}



/** True when the contact is flagged as on title (contact-type checkbox). */

export function customerIsOnTitle(customer: unknown): boolean {

    const row = asRecord(customer);

    if (!row) return false;

    return Boolean(row.OnTitleCheck || row.onTitleCheck);

}



/** Black Knight / title-verified history — used for title sync tiles and Add Title flows. */

export function customerHasTitleRecord(customer: unknown): boolean {

    const row = asRecord(customer);

    if (!row) return false;

    return Boolean(

        customerIsOnTitle(row) ||

            row.TitleVerified ||

            row.titleVerified ||

            row.BKTitleCheckMade ||

            row.bkTitleCheckMade,

    );

}



export function getOpportunityCustomers(opportunity: unknown): Record<string, unknown>[] {

    const row = asRecord(opportunity);

    const customers = row?.Customers ?? row?.customers;

    if (!Array.isArray(customers)) return [];

    return customers.filter((c): c is Record<string, unknown> => Boolean(asRecord(c)));

}



export function getOnTitleCustomers(opportunity: unknown): Record<string, unknown>[] {

    return getOpportunityCustomers(opportunity).filter(customerIsOnTitle);

}



export function getTitleRecordCustomers(opportunity: unknown): Record<string, unknown>[] {

    return getOpportunityCustomers(opportunity).filter(customerHasTitleRecord);

}



export function opportunityHasSyncedTitleContact(opportunity: unknown): boolean {

    return getOnTitleCustomers(opportunity).length > 0;

}



function customerDisplayName(customer: Record<string, unknown>): string {

    const first = String(customer.FirstName ?? customer.firstName ?? '').trim();

    const last = String(customer.LastName ?? customer.lastName ?? '').trim();

    const combined = [first, last].filter(Boolean).join(' ');

    if (combined) return combined;

    return String(customer.Name ?? customer.name ?? '').trim();

}



export function syncedTitleContactName(opportunity: unknown): string {

    return getOnTitleCustomers(opportunity)

        .map(customerDisplayName)

        .filter(Boolean)

        .join(', ');

}



export type TitleSyncCustomerRef = {

    opportunityContactId?: string | null;

    isUpdate?: boolean;

    firstName?: string;

    lastName?: string;

};



function namesMatchTitleSync(

    customerFirst: string,

    customerLast: string,

    syncFirst: string,

    syncLast: string,

): boolean {

    if (!syncFirst || !syncLast) return false;

    if (syncFirst === customerFirst && syncLast === customerLast) return true;

    // Title records sometimes swap first/last (e.g. "SINGLETON LINDA" vs "Linda Singleton").

    return syncFirst === customerLast && syncLast === customerFirst;

}



export function customerMatchesTitleSync(

    customer: Record<string, unknown>,

    sync: TitleSyncCustomerRef,

): boolean {

    const syncId = sync.opportunityContactId != null ? String(sync.opportunityContactId).trim() : '';

    const customerNumericId = opportunityContactNumericId(customer);

    if (syncId && customerNumericId != null && syncId === String(customerNumericId)) {

        return true;

    }



    const syncFirst = normalizePersonName(sync.firstName);

    const syncLast = normalizePersonName(sync.lastName);

    const customerFirst = normalizePersonName(customer.FirstName ?? customer.firstName);

    const customerLast = normalizePersonName(customer.LastName ?? customer.lastName);

    return namesMatchTitleSync(customerFirst, customerLast, syncFirst, syncLast);

}



function withTitleSyncFlags(customer: Record<string, unknown>): Record<string, unknown> {

    return {

        ...customer,

        OnTitleCheck: true,

        onTitleCheck: true,

        TitleVerified: true,

        titleVerified: true,

        BKTitleCheckMade: true,

        bkTitleCheckMade: true,

    };

}



function findOptimisticCustomerMatch(

    fetchedCustomer: Record<string, unknown>,

    optimisticCustomers: Record<string, unknown>[],

): Record<string, unknown> | undefined {

    const fetchedId = opportunityContactNumericId(fetchedCustomer);

    if (fetchedId != null) {

        const byId = optimisticCustomers.find((c) => opportunityContactNumericId(c) === fetchedId);

        if (byId) return byId;

    }



    const fetchedFirst = normalizePersonName(fetchedCustomer.FirstName ?? fetchedCustomer.firstName);

    const fetchedLast = normalizePersonName(fetchedCustomer.LastName ?? fetchedCustomer.lastName);

    return optimisticCustomers.find((c) => {

        const first = normalizePersonName(c.FirstName ?? c.firstName);

        const last = normalizePersonName(c.LastName ?? c.lastName);

        return namesMatchTitleSync(first, last, fetchedFirst, fetchedLast);

    });

}



/** Optimistically mark synced title contacts as ON TITLE in opportunity customers. */

export function applyTitleSyncToOpportunityCustomers(

    opportunity: unknown,

    syncedContacts: TitleSyncCustomerRef[],

): Record<string, unknown> | null {

    const opp = asRecord(opportunity);

    if (!opp || syncedContacts.length === 0) return null;



    const customers = getOpportunityCustomers(opp);

    if (customers.length === 0) return opp;



    let changed = false;

    const updatedCustomers = customers.map((customer) => {

        const matched = syncedContacts.some((sync) => customerMatchesTitleSync(customer, sync));

        if (!matched) return customer;



        changed = true;

        return withTitleSyncFlags(customer);

    });



    if (!changed) return opp;

    return { ...opp, Customers: updatedCustomers };

}



/** Merge title-sync flags onto opportunity customers returned from OpportunityDetail. */

export function mergeOpportunityCustomersAfterTitleSync(

    opportunity: unknown,

    syncedContacts: TitleSyncCustomerRef[],

): Record<string, unknown> | null {

    const patched = applyTitleSyncToOpportunityCustomers(opportunity, syncedContacts);

    const opp = patched ?? asRecord(opportunity);

    if (!opp) return null;



    const customers = getOpportunityCustomers(opp);

    if (customers.length === 0) return opp;



    let changed = patched != null;

    const updatedCustomers = customers.map((customer) => {

        const synced = syncedContacts.some((sync) => customerMatchesTitleSync(customer, sync));

        const titleVerifiedFromApi = customerIsOnTitle(customer) || customerHasTitleRecord(customer);

        if (!synced && !titleVerifiedFromApi) return customer;

        if (customerIsOnTitle(customer)) return customer;

        changed = true;

        return withTitleSyncFlags(customer);

    });



    if (!changed) return opp;

    return { ...opp, Customers: updatedCustomers };

}



/**

 * Re-apply ON TITLE flags from the pre-fetch optimistic patch when the API reload

 * returns stale customer rows (common immediately after title sync).

 */

export function preserveTitleSyncFlagsAfterReload(

    optimisticOpportunity: unknown,

    fetchedOpportunity: unknown,

): Record<string, unknown> | null {

    const fetched = asRecord(fetchedOpportunity);

    if (!fetched) return null;



    const optimisticCustomers = getOpportunityCustomers(optimisticOpportunity).filter(customerIsOnTitle);

    if (optimisticCustomers.length === 0) return fetched;



    const customers = getOpportunityCustomers(fetched);

    if (customers.length === 0) return fetched;



    let changed = false;

    const updatedCustomers = customers.map((customer) => {

        if (customerIsOnTitle(customer)) return customer;

        const optimisticMatch = findOptimisticCustomerMatch(customer, optimisticCustomers);

        if (!optimisticMatch || !customerIsOnTitle(optimisticMatch)) return customer;

        changed = true;

        return withTitleSyncFlags(customer);

    });



    if (!changed) return fetched;

    return { ...fetched, Customers: updatedCustomers };

}


